import type { ReactNode } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Alert, Form, FormSection, Heading } from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import type {
    IAltoDermaOthProcedureAddValidated,
    IAltoDermaOthProcedureCustomControlData,
} from '../../../../common/validation/customControls/dermatology';
import type { ISchemaContainer } from '../../../../common/validation/ISchemaContainer';
import type { ValidationErrors } from '../../../utils/validation';
import { PatientDataError } from '../../../../common/enums/PatientDataError';
import { ProcedureStatus } from '../../../../common/enums/ProcedureStatus';
import { ServiceCode } from '../../../../common/enums/ServiceCode';
import {
    altoDermaOthProcedureSchema,
    bsnLoginSchema,
    step2RegularSchema,
    step3RegularSchema,
    step4RegularSchema,
    whoAreYouSchema,
} from '../../../../common/validation/customControls/dermatology';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { BackButton } from '../../elements/atoms/backButton/BackButton';
import { Paragraph } from '../../elements/atoms/paragraph/Paragraph';
import { Main } from '../../elements/molecules/main/Main';
import { ProgressBar } from '../../elements/molecules/progressBar/ProgressBar';
import { Footer } from '../../elements/organisms/footer/Footer';
import { Header } from '../../elements/organisms/header/Header';
import { PageWrapper } from '../../elements/organisms/pageWrapper/PageWrapper';
import { BsnLogin } from './steps/alto/BsnLogin';
import { ProcedureSentMessage } from './steps/alto/ProcedureSent';
import { WhoAreYou } from './steps/alto/WhoAreYou';
import { Intro } from './steps/common/Intro';
import { Step1 } from './steps/common/Step1';
import { Step2 } from './steps/common/Step2';
import { Step3 } from './steps/common/Step3';
import { Step4 } from './steps/common/Step4';
import { Step5 } from './steps/common/Step5';

export interface IAltoStepProps {
    csPatientName?: string | null;
    errors: ValidationErrors;
    handleChange: (update: Partial<IAltoDermaOthProcedureCustomControlData>) => void;
    isSubmitting?: boolean;
    onUploadStatusChanged?: (isUploading: boolean) => void;
    procedure: IAltoDermaOthProcedureAddValidated;
    submit?: () => void;
    tokenId: string | null;
    validateAndGoToNextStep: () => Promise<void>;
}

const StyledContainer = styled.div`
    @media screen and (min-width: 576px) {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        width: 100%;
        flex-direction: column;
    }
`;

const StyledForm = styled(Form)`
    width: 100%;
    padding: 56px 16px;

    @media screen and (min-width: 576px) {
        width: 544px;
        padding: 56px;
    }
`;

const StyledLogo = styled.div`
    width: 120px;
    height: 37px;
    margin: 0 auto 16px auto;
    img {
        max-width: 100%;
        height: auto;
        display: block;
    }
`;

export function NewAltoDermaOthProcedure() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tokenId = searchParams.get('token');
    const [csPatientName, setCsPatientName] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [areValidationsActivated, setAreValidationsActivated] = useState<boolean>(false);
    const [isInvitationValid, setIsInvitationValid] = useState<boolean | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [procedure, setProcedure] = useState<IAltoDermaOthProcedureAddValidated>({
        customControlData: {
            condition: {
                anders: null,
                jeukt: false,
                pijn: false,
            },
            conditionBefore: null,
            conditionBeforeRemark: null,
            conditionChanged: null,
            conditionColorChanged: null,
            conditionSizeChanged: null,
            conditionStarted: null,
            detailFiles: [],
            extraQuestion: null,
            overviewFiles: [],
            patient: {
                bsn: null,
                isSomeoneElse: null,
                someoneElseName: null,
            },
            treatingCondition: null,
            treatingConditionRemark: null,
        },
    });
    const numberOfLoginSteps = 2;

    useEffect(() => {
        if (areValidationsActivated) {
            validateOnChange();
        }
    }, [procedure]);

    useEffect(() => {
        getAltoProcedure();
    }, []);

    async function getAltoProcedure() {
        try {
            const url = `/api/patient/alto/${tokenId}/procedure`;
            const result = await fetchJson(url, 'GET');

            if (result.json.status === ProcedureStatus.active) {
                if (DateTime.now().isAfter(DateTime.from(result.json.invitationSentAt).add(1, 'day'))) {
                    setIsInvitationValid(false);
                } else {
                    setProcedure({ ...procedure, procedureId: result.json.procedureId });
                    setCsPatientName(result.json.patient.name);
                    setIsInvitationValid(true);
                }
            } else {
                navigate(`/saml/login?error=${PatientDataError.procedureSent}`);
            }
        } catch {
            navigate(`/saml/login?error=${PatientDataError.invalidUrl}`);
        }
    }

    function getValidationSchemaByStep(step: number) {
        switch (step) {
            case 0:
                return whoAreYouSchema;
            case 1:
                return bsnLoginSchema;
            case 2:
                return null;
            case 3:
                return null;
            case 4:
                return step2RegularSchema;
            case 5:
                return step3RegularSchema;
            case 6:
                return step4RegularSchema;
            case 7:
                return null;
            default:
                throw new Error('Schema not defined for step ' + step);
        }
    }

    async function validateAndGoToNextStep() {
        setAreValidationsActivated(true);
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = getValidationResult(schema);
            if (validationResult.errors.size === 0) {
                if (currentStep === 1 && (await isBsnDifferent())) {
                    setErrors(errors.set('bsn', 'differentBsn'));
                } else {
                    goToNextStep();
                }
            } else {
                setErrors(validationResult.errors);
            }
        } else {
            goToNextStep();
        }
    }

    async function isBsnDifferent() {
        const url = `/api/patient/alto/${tokenId}/procedure`;
        const result = await fetchJson(url, 'GET');
        if (result.json.patient.bsn !== procedure.customControlData.patient.bsn) {
            return true;
        }

        return false;
    }

    function validateOnChange() {
        const schema = getValidationSchemaByStep(currentStep);
        if (schema) {
            const validationResult = getValidationResult(schema);
            setErrors(validationResult.errors);
        }
    }

    function getValidationResult(schema: ISchemaContainer<unknown>) {
        let validationResult;
        if (currentStep <= 1) {
            validationResult = validate(schema, procedure.customControlData.patient);
        } else {
            validationResult = validate(schema, procedure.customControlData);
        }

        return validationResult;
    }

    function goToNextStep() {
        setAreValidationsActivated(false);
        setErrors(emptyValidationErrors);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    }

    function handleChange(update: Partial<IAltoDermaOthProcedureCustomControlData>) {
        setProcedure({ ...procedure, customControlData: { ...procedure.customControlData, ...update } });
    }

    async function submitProcedure() {
        setIsSubmitting(true);
        const validationResult = validate(altoDermaOthProcedureSchema, procedure);
        if (validationResult.errors.size === 0) {
            await fetchJson(
                `/api/patient/alto/homeExamination?tokenId=${tokenId}&serviceCode=${ServiceCode.DermaOth}`,
                'POST',
                getPostData()
            );
            goToNextStep();
        } else {
            setIsSubmitting(false);
        }
    }

    function goToPrevStep() {
        if (currentStep > numberOfLoginSteps) {
            setCurrentStep(currentStep - 1);
        }
    }

    function getPostData() {
        const result = JSON.parse(JSON.stringify(procedure));

        delete result.customControlData.overviewFiles[0].thumbnailDataUrl;
        delete result.customControlData.overviewFiles[0].placement;
        for (const detailFile of result.customControlData.detailFiles) {
            delete detailFile.thumbnailDataUrl;
            delete detailFile.placement;
        }

        return { procedure: result };
    }

    function getTitleForStep(currentStepForTitle: number) {
        let titleForStep;
        switch (currentStepForTitle) {
            case 0:
                titleForStep = 'Welkom';
                break;
            case 1:
                titleForStep = 'Controle';
                break;
            default:
                titleForStep = '';
                break;
        }

        return titleForStep;
    }

    const stepProps = {
        errors,
        handleChange,
        procedure,
        tokenId,
        validateAndGoToNextStep,
    };

    const steps: ReactNode[] = [
        <WhoAreYou
            key={0}
            {...stepProps}
            csPatientName={csPatientName}
        />,
        <BsnLogin
            key={1}
            {...stepProps}
            csPatientName={csPatientName}
        />,
        <Intro
            key={2}
            {...stepProps}
            altoFlow
        />,
        <Step1
            key={3}
            {...stepProps}
            altoFlow
        />,
        <Step2
            key={4}
            {...stepProps}
            onUploadStatusChanged={(uploadingStatus) => setIsUploading(uploadingStatus)}
        />,
        <Step3
            key={5}
            {...stepProps}
            onUploadStatusChanged={(uploadingStatus) => setIsUploading(uploadingStatus)}
        />,
        <Step4
            key={6}
            {...stepProps}
            altoFlow={true}
            csPatientName={csPatientName}
        />,
        <Step5
            key={7}
            {...stepProps}
            altoFlow={true}
            csPatientName={csPatientName}
            isSubmitting={isSubmitting}
            submit={submitProcedure}
        />,
        <ProcedureSentMessage key={8} />,
    ];

    if (isInvitationValid === null) {
        return null;
    } else if (!isInvitationValid) {
        return (
            <StyledContainer>
                <StyledForm submitOnEnter={true}>
                    <FormSection isBorderless>
                        <StyledLogo>
                            <img
                                alt="logo"
                                src="/assets/images/logo-ksyos.png"
                            />
                        </StyledLogo>
                    </FormSection>
                    <FormSection isBorderless>
                        <Alert appearance="warning">
                            Wat jammer, de uitnodiging voor de vragenlijst is helaas verlopen.
                        </Alert>
                        <Paragraph>
                            Geen probleem, we lossen het op. Bel je huisarts om een nieuwe uitnodiging te ontvangen. De
                            uitnodiging blijft 24 uur geldig. Daarna verloopt de uitnodiging.
                        </Paragraph>
                    </FormSection>
                </StyledForm>
            </StyledContainer>
        );
    }

    const backButton = (
        <BackButton
            disabled={isUploading}
            goBackFunction={goToPrevStep}
        />
    );

    return (
        <>
            {currentStep < numberOfLoginSteps && (
                <StyledContainer>
                    <StyledForm submitOnEnter={true}>
                        <FormSection isBorderless>
                            <StyledLogo>
                                <img
                                    alt="logo"
                                    src="/assets/images/logo-ksyos.png"
                                />
                            </StyledLogo>
                            <Heading
                                size="xlarge"
                                textAlign="center"
                            >
                                {getTitleForStep(currentStep)}
                            </Heading>
                        </FormSection>
                        <FormSection isBorderless>{steps[currentStep]}</FormSection>
                    </StyledForm>
                </StyledContainer>
            )}
            {currentStep >= numberOfLoginSteps && currentStep < steps.length && (
                <PageWrapper
                    fullHeight
                    scrollToTopUpdater={currentStep}
                >
                    <Header
                        customBackButton={backButton}
                        disableLogoLink={true}
                        hasBackbutton={
                            currentStep > numberOfLoginSteps && currentStep < steps.length - 1 && !isSubmitting
                        }
                    />
                    {currentStep > numberOfLoginSteps && currentStep < steps.length - 1 && (
                        <ProgressBar progress={(currentStep / (steps.length - 1)) * 100}></ProgressBar>
                    )}
                    <Main>{currentStep >= numberOfLoginSteps && currentStep < steps.length && steps[currentStep]}</Main>
                    <Footer />
                </PageWrapper>
            )}
        </>
    );
}
